import { useEffect, useRef, useState } from 'react';
import { useForm } from '@mantine/form';
import { Alert, Box, Button, Group, Select, Text, TextInput, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { plantsApi } from '../../services/plantsApi';
import { plantKilnsApi } from '../../services/plantKilnsApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import { statusPalette } from '../../theme/colors';
import { Region, RegionLabels } from '../../types/entities';
import type { Company, Plant, PlantFormData, PlantKiln, PlantKilnFormData } from '../../types/entities';
import { KilnRows, type KilnRowState } from './KilnRows';
import { toIsoDate } from './kilnActivity';
import { plantActivationNotice } from './plantActivationNotice';
import type { DirtyGuardApi } from './useDirtyGuard';
import { prefixViolationPaths } from './violationPaths';
import classes from './PlantDetail.module.css';

interface PlantDetailProps {
  plant: Plant;
  company: Company;
  reload: () => Promise<void>;
  onDeleted: (companyId: number) => void;
  /** Sprung aus der verlinkten Werkszeile im Unternehmensdetail (Plan 28-07, UI-SPEC Punkt 8) --
   * nur ein erhoehter Token bewegt den Fokus auf die Ueberschrift, eine gewoehnliche Baumauswahl
   * nicht (sonst wuerde jeder Tree-Klick die Pfeiltasten-Navigation im Baum unterbrechen). */
  focusToken: number;
  /** Plan 28-08 Task 3: meldet den aktuellen Vergleichszustand an den Ebenenwechsel-Schutz in
   * `MasterDataPage` (UI-SPEC Punkt 5). */
  dirtyGuard: DirtyGuardApi;
}

/** Werte des Werksformulars: die Felder des Werks plus die Ofenzeilen als Liste. Beides in EINEM
 * Formular, damit ein Speichern-Knopf und ein `isDirty()` die ganze Ebene abdecken. */
interface PlantFormValues {
  name: string;
  region: Region | '';
  // D-13: drittes Feld "Stillgelegt zum" neben Name und Region, leerbar.
  activeUntil: string | null;
  kilns: KilnRowState[];
}

/** Zusammenfassende Alert-Anzeige des Werksdetails -- deckt Speicher- UND Loeschfehler ab (Sketch
 * 027 `alertHtml`); `action: 'closure'` blendet den Knopf ein, der ins Datumsfeld springt. */
interface PlantAlert {
  title?: string;
  message: string;
  action?: 'closure';
}

function toKilnRowState(kiln: PlantKiln): KilnRowState {
  return {
    key: `existing-${kiln.id}`,
    id: kiln.id,
    name: kiln.name,
    activeFrom: kiln.activeFrom ?? null,
    activeUntil: kiln.activeUntil ?? null,
    allowsOwnMeasurements: kiln.allowsOwnMeasurements,
  };
}

/** Hat sich diese Ofenzeile gegenueber ihrem geladenen Stand geaendert? Entscheidet allein, ob
 * die Zeile einen eigenen Request braucht -- ob das FORMULAR Aenderungen hat, beantwortet
 * `form.isDirty()`. */
function kilnRowChanged(current: KilnRowState, loaded: KilnRowState): boolean {
  return (
    current.name !== loaded.name ||
    (current.activeFrom || null) !== (loaded.activeFrom || null) ||
    (current.activeUntil || null) !== (loaded.activeUntil || null) ||
    current.allowsOwnMeasurements !== loaded.allowsOwnMeasurements
  );
}

function toFormValues(plant: Plant): PlantFormValues {
  // Reiner Datumsteil (kein Zeitzonenanteil): damit haengt `isDirty()` nicht an der vom Server
  // gelieferten Zeitzonenangabe, sondern nur am fachlichen Datum.
  const activeUntil = plant.activeUntil ? toIsoDate(plant.activeUntil) : null;
  return {
    name: plant.name,
    region: plant.region ?? '',
    activeUntil,
    kilns: (plant.kilns ?? []).map(toKilnRowState),
  };
}

/**
 * Werksdetail (D-01, D-02, D-04, D-06, D-08; UI-SPEC Punkte 3-6) -- Aufbau woertlich nach
 * `renderDetailB` aus Sketch 008 Variante B, gespiegelt an `CompanyDetail.tsx` (Plan 28-07):
 * eigener saving-/formError-Zustand (D-02, jede Ebene speichert fuer sich), Speichern-Knopf
 * `disabled` solange die Felder dem geladenen Stand entsprechen, konstantes Label ausser waehrend
 * des Requests. Die Ofenzeilen selbst kommen aus `KilnRows` (Plan 28-08 Task 2).
 */
export function PlantDetail({
  plant,
  company,
  reload,
  onDeleted,
  focusToken,
  dirtyGuard,
}: PlantDetailProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const appliedTokenRef = useRef(0);
  const newRowCounterRef = useRef(0);
  // D-03/D-08: Sprungziel des Alert-Aktionsknopfs bzw. des 422-Fehlers am Datumsfeld selbst.
  const activeUntilRef = useRef<HTMLInputElement>(null);

  // D-03/D-08: EIN Zustand fuer Speicher- und Loeschfehler (Sketch 027 `alertHtml`), statt eines
  // reinen Textstrings -- traegt optional Titel und den Knopf zum Datumsfeld.
  const [alert, setAlert] = useState<PlantAlert | null>(null);
  const [saving, setSaving] = useState(false);

  // Siehe CompanyDetail: `controlled`, weil der Speichern-Knopf an `isDirty()` haengt.
  const form = useForm<PlantFormValues>({
    mode: 'controlled',
    initialValues: toFormValues(plant),
  });

  useEffect(() => {
    if (focusToken > appliedTokenRef.current) {
      appliedTokenRef.current = focusToken;
      headingRef.current?.focus();
    }
  }, [focusToken]);

  const isDirtyState = form.isDirty();

  // Meldet bei jedem Render an den Ebenenwechsel-Schutz (UI-SPEC Punkt 5), ob diese Ebene
  // ungespeicherte Aenderungen traegt -- Werksfelder UND Ofenzeilen, beide liegen im selben
  // Formular.
  useEffect(() => {
    dirtyGuard.track('Werk', plant.name, isDirtyState);
    return () => dirtyGuard.clear();
  });

  function handleDiscard() {
    form.reset();
    setAlert(null);
  }

  function handleAddKilnRow() {
    newRowCounterRef.current += 1;
    form.insertListItem('kilns', {
      key: `new-${newRowCounterRef.current}`,
      name: '',
      activeFrom: null,
      activeUntil: null,
      allowsOwnMeasurements: true,
    });
  }

  /** D-03: springt aus dem Alert-Knopf bzw. der 422-Antwort ins Datumsfeld. */
  function focusActiveUntilField() {
    setAlert(null);
    activeUntilRef.current?.scrollIntoView({ block: 'center' });
    activeUntilRef.current?.focus();
  }

  async function handleSubmit(values: PlantFormValues) {
    setAlert(null);
    setSaving(true);
    // Merkt sich, welche Zeile gerade dran ist: schlaegt ihr Request fehl, gehen die Feldfehler
    // aus der Antwort an genau diese Zeile (`kilns.<index>.<feld>`) und nicht an das Werk.
    let failingKilnIndex: number | null = null;
    try {
      const payload: Partial<PlantFormData> = { name: values.name, activeUntil: values.activeUntil };
      if (values.region) {
        payload.region = values.region;
      }
      const saved = await plantsApi.update(plant.id, payload);

      // D-16: die Folgen der Stilllegung (entfernte/neu angelegte Meldungen) sind mit dem
      // Plant-PATCH bereits eingetreten -- die Notification zeigt sie deshalb sofort danach, auch
      // wenn ein nachfolgender Ofen-Request scheitert.
      const notice = plantActivationNotice(saved.activationEffects);
      notifications.show({ title: notice.title, message: notice.message, color: 'vdzGray' });

      // Je geaenderter Ofenzeile ein eigener plantKilnsApi-Aufruf, nacheinander (UI-SPEC
      // Copywriting-Tabelle, D-02): schlaegt einer fehl, bleiben die zuvor gespeicherten Zeilen
      // gespeichert -- kein Rollback fremder Ebenen.
      const loadedKilns = form.getInitialValues().kilns;
      for (let index = 0; index < values.kilns.length; index += 1) {
        const row = values.kilns[index];
        // `form.isDirty('kilns.N')` taugt hier nicht: nach einer eingefuegten oder entfernten
        // Zeile vergleicht Mantine den Listenplatz, nicht die Zeile. Der stabile `key` sagt,
        // welcher geladene Stand gemeint ist.
        const loadedRow = loadedKilns.find((r) => r.key === row.key);
        if (loadedRow && !kilnRowChanged(row, loadedRow)) {
          continue;
        }
        failingKilnIndex = index;
        if (row.id) {
          await plantKilnsApi.update(row.id, {
            name: row.name,
            activeFrom: row.activeFrom,
            activeUntil: row.activeUntil,
            allowsOwnMeasurements: row.allowsOwnMeasurements,
          });
        } else {
          const created: PlantKilnFormData = {
            name: row.name,
            plant: `/api/plants/${plant.id}`,
            sortOrder: index,
            activeFrom: row.activeFrom,
            activeUntil: row.activeUntil,
            allowsOwnMeasurements: row.allowsOwnMeasurements,
          };
          await plantKilnsApi.create(created);
        }
        failingKilnIndex = null;
      }

      await reload();
    } catch (err) {
      const violations = getApiViolations(err);
      const prefixedViolations =
        failingKilnIndex === null ? violations : prefixViolationPaths(violations, `kilns.${failingKilnIndex}`);
      form.setErrors(mapViolations(prefixedViolations));
      // D-08: tragen die Verstoesse einen bekannten Feldpfad (Werksfelder oder eine Ofenzeile),
      // zeigt der Sammel-Alert den festen Hinweistext -- sonst den Backend-Fehler wie bisher.
      const touchesKnownField = prefixedViolations.some(
        (violation) =>
          violation.propertyPath === 'name' ||
          violation.propertyPath === 'region' ||
          violation.propertyPath === 'activeUntil' ||
          violation.propertyPath.startsWith('kilns.')
      );
      setAlert(
        touchesKnownField
          ? { title: 'Werk nicht gespeichert.', message: 'Bitte die markierten Felder prüfen.' }
          : { message: getApiErrorMessage(err, 'Fehler beim Speichern') }
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    try {
      await plantsApi.delete(plant.id);
      // Sketch 027 `m-ok`-Handler: Erfolg als Toast, noch vor dem Aushaengen der Detailansicht.
      notifications.show({ message: `${plant.name} gelöscht.`, color: 'vdzGray' });
      onDeleted(company.id);
      await reload();
    } catch (err) {
      // D-03: 422 heisst Loeschschutz -- Alert mit Backend-Text und dem Knopf zum Datumsfeld.
      // Andere Fehler (Netzwerk, 500er) bleiben ein einfacher Alert ohne Aktion.
      if (isAxiosError(err) && err.response?.status === 422) {
        setAlert({
          title: `${plant.name} lässt sich nicht löschen.`,
          message: getApiErrorMessage(err, 'Das Werk lässt sich nicht löschen.'),
          action: 'closure',
        });
      } else {
        setAlert({ message: getApiErrorMessage(err, 'Fehler beim Löschen') });
      }
    }
  }

  function handleDeleteClick() {
    modals.openConfirmModal({
      title: 'Werk löschen',
      children: (
        <>
          <Text size="sm">
            {plant.name} wird mit seinen Drehöfen und allen offenen Meldungen endgültig gelöscht.
          </Text>
          <Text size="sm" c="vdzGray.6" mt="xs">
            Das ist für versehentlich angelegte Werke gedacht. Hat das Werk schon eingereichte oder
            freigegebene Meldungen, lässt es sich nicht löschen –{' '}
            tragen Sie dann ein Stilllegungsdatum ein.
          </Text>
        </>
      ),
      labels: { confirm: 'Löschen', cancel: 'Abbrechen' },
      confirmProps: { color: 'red' },
      onConfirm: handleDelete,
    });
  }

  return (
    <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
      <Group gap={6} mb={2}>
        <Text size="sm" c="vdzGray.6">
          {company.name}
        </Text>
        {company.csiMember && (
          <Text
            component="span"
            fw={700}
            fz={12}
            px={7}
            py={1}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              backgroundColor: statusPalette.inPruefung.bg,
              color: statusPalette.inPruefung.fg,
              border: `1px solid ${statusPalette.inPruefung.border}`,
              whiteSpace: 'nowrap',
            }}
          >
            CSI
          </Text>
        )}
      </Group>
      <Title order={2} ref={headingRef} tabIndex={-1}>
        {plant.name}
      </Title>

      {alert && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title={alert.title} mt="md">
          {alert.message}
          {alert.action === 'closure' && (
            <Box mt="xs">
              <Button size="xs" variant="default" onClick={focusActiveUntilField}>
                Stilllegungsdatum eintragen
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Box mt="md" className={classes.fields}>
        <TextInput label="Name" required {...form.getInputProps('name')} />
        {/* Pflichtfeld: `plant.region` ist NOT NULL und traegt `Assert\NotNull` (Plant.php:60) --
            das Sternchen sagt hier nur, was Datenmodell und Validierung ohnehin verlangen. */}
        <Select
          label="Region"
          placeholder="— bitte wählen —"
          data={Object.values(Region).map((region) => ({ value: region, label: RegionLabels[region] }))}
          required
          {...form.getInputProps('region')}
          value={form.getValues().region || null}
          onChange={(value) => form.setFieldValue('region', (value as Region) || '')}
        />
        {/* D-13: drittes Feld neben Name/Region, leerbar -- kein Pflichtstern, keine Zusatzzeile
            unter dem Titel, kein Chip, keine Meldeperioden-Leiste (Sketch 027 `dateField`,
            Variante D). */}
        <DateInput
          ref={activeUntilRef}
          label="Stillgelegt zum"
          placeholder="TT.MM.JJJJ"
          valueFormat="DD.MM.YYYY"
          locale="de"
          clearable
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          description={form.errors.activeUntil ? undefined : 'leer = in Betrieb'}
          {...form.getInputProps('activeUntil')}
        />
      </Box>

      <Box mt={26}>
        <Group justify="space-between" align="center" mb="sm" wrap="nowrap">
          <Text fw={700} size="sm">
            Drehöfen ({form.getValues().kilns.length})
          </Text>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddKilnRow}
          >
            Ofen hinzufügen
          </Button>
        </Group>
        <KilnRows form={form} plantActiveUntil={plant.activeUntil ?? null} />
      </Box>

      {/* Kein eigener Trennstrich: die Tabelle darueber schliesst bereits mit einer Kante ab,
          zwei Linien im Abstand einer Zeilenhoehe lasen sich wie ein leerer Tabelleneintrag. */}
      <Group justify="space-between" mt="xl" pt="md">
        <Button variant="outline" color="red" onClick={handleDeleteClick}>
          Werk löschen
        </Button>
        <Group>
          <Button variant="default" onClick={handleDiscard}>
            Verwerfen
          </Button>
          <Button type="submit" disabled={!isDirtyState || saving} loading={saving}>
            {saving ? 'Speichert…' : 'Werk speichern'}
          </Button>
        </Group>
      </Group>
    </Box>
  );
}
